import { useLogout } from "../../../hooks/useLogout";
import { PinPad } from "../../../components/pinPad";
import { AccessSelector } from "../../../components/accessSelector";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BsTv } from "react-icons/bs";
import "./style.css";
import { checkPassword, getPointsConfigurations, logIn, setPointConfigurations } from "../../../api/login";
import {
  setComponentActive,
  setUserConfigData,
  setUserData,
  setUserLogged,
  updateCorporationPointConfig,
} from "../../../reducers/userConfigSlice";
import { readLoginLS, saveLoginLS } from "@delibrandev/utils";
import { apiRest } from "../../../api/constants";
import { decodeToken } from "../../../api/utils";

export const UserSelect = ({ defaultMenu = "password" }) => {
  const dispatch = useDispatch();
  const { logout: logOff, busy: loggingOut } = useLogout();
  const point = useSelector((state) => state.aplicationConfig.corporationData?.pointConfig || {});
  const info = useSelector((state) => state.aplicationConfig.corporationData?.info || {});
  const businessName = info.corporation?.name || info.name || "Tu negocio";
  const assignedUsers = useMemo(
    () => (Array.isArray(point.assignedUsers) ? point.assignedUsers : []),
    [point.assignedUsers],
  );
  const [menu, setMenu] = useState(defaultMenu);
  const [userData, setUserDataLocal] = useState(null);
  const [orderManagers, setOrderManagers] = useState([]);
  const [loginData, updateLoginData] = useState({});
  const [accessError, setAccessError] = useState("");
  const [pending, setPending] = useState(false);
  const [pointsPending, setPointsPending] = useState(false);
  const busyRef = useRef(false);
  const pointsRef = useRef(false);
  const runSafe = useCallback(async (fn) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setPending(true);
    setAccessError("");
    try {
      return await fn();
    } finally {
      busyRef.current = false;
      setPending(false);
    }
  }, []);

  const fetchOrderManagers = useCallback(async () => {
    if (pointsRef.current) return;
    pointsRef.current = true;
    setPointsPending(true);
    setAccessError("");
    try {
      const response = await getPointsConfigurations();
      if (response?.error !== false || !Array.isArray(response.data)) throw new Error("Unconfirmed station list");
      setOrderManagers(response.data);
    } catch {
      setAccessError("No se han podido cargar los puestos. Comprueba la conexión y vuelve a intentarlo.");
    } finally {
      pointsRef.current = false;
      setPointsPending(false);
    }
  }, []);
  useEffect(() => {
    setMenu(defaultMenu);
    setAccessError("");
  }, [defaultMenu]);
  useEffect(() => {
    if (menu === "orderManager") void fetchOrderManagers();
  }, [menu, fetchOrderManagers]);

  const handleLogout = async () => {
    if (await logOff()) {
      setMenu("password");
      setUserDataLocal(null);
      updateLoginData({});
      setOrderManagers([]);
      setAccessError("");
    }
  };
  const handleUser = (pinValue) =>
    runSafe(async () => {
      if (!userData?.userId) {
        setAccessError("Selecciona un empleado para continuar.");
        return false;
      }
      try {
        const payload = { userId: userData.userId, pin: pinValue };
        const response = await logIn(payload);
        if (response?.data?.error === false && response.data.data) {
          const decoded = decodeToken(response.data.data);
          if (!decoded?.userId) throw new Error("Unconfirmed operator identity");
          saveLoginLS({ ...readLoginLS(apiRest), ...loginData, login: payload }, apiRest);
          dispatch(setUserData(decoded));
          dispatch(setUserLogged(true));
          dispatch(setComponentActive("commander"));
          return true;
        }
        const message = response?.data?.message || response?.message || "";
        setAccessError(
          message === "PIN is not active."
            ? "El PIN está desactivado. Solicita acceso al administrador."
            : /access|permited/i.test(message)
              ? "No tienes permiso para este TPV o puesto."
              : /pin isnt correct/i.test(message)
                ? "PIN incorrecto. Revisa tu PIN de empleado e inténtalo de nuevo."
                : "No se ha podido comprobar el PIN. Vuelve a intentarlo.",
        );
        return false;
      } catch {
        setAccessError("No se ha podido comprobar el PIN. Comprueba la conexión y vuelve a intentarlo.");
        return false;
      }
    });
  const handlePassword = (password) =>
    runSafe(async () => {
      try {
        const response = await checkPassword({ password });
        const result = typeof response?.data?.error === "boolean" ? response.data : response;
        if (result?.error !== false) {
          setAccessError(
            /Incorrect password|Contraseña incorrecta/i.test(result?.message || "")
              ? "Código de acceso al TPV incorrecto. Este código es distinto del PIN del empleado."
              : "No se ha podido comprobar el acceso. Vuelve a intentarlo.",
          );
          return false;
        }
        updateLoginData((prev) => ({ ...prev, password }));
        const rememberedPoint = readLoginLS(apiRest)?.point;
        if (rememberedPoint?.PointOfSaleConfigurationId) {
          const points = await getPointsConfigurations();
          const selectedPoint =
            points?.error === false && Array.isArray(points.data)
              ? points.data.find(
                  (item) => item.PointOfSaleConfigurationId === rememberedPoint.PointOfSaleConfigurationId,
                )
              : null;
          if (selectedPoint) {
            const selected = await setPointConfigurations({
              pointOfSaleConfigurationId: selectedPoint.PointOfSaleConfigurationId,
            });
            if (selected?.error === false) {
              dispatch(updateCorporationPointConfig(selectedPoint));
              updateLoginData((prev) => ({ ...prev, point: selectedPoint }));
              setMenu("users");
              return true;
            }
          }
        }
        setMenu("orderManager");
        return true;
      } catch {
        setAccessError("No se ha podido comprobar el acceso. Comprueba la conexión y vuelve a intentarlo.");
        return false;
      }
    });
  const selectPoint = (selectedPoint) =>
    runSafe(async () => {
      try {
        const id = selectedPoint?.PointOfSaleConfigurationId;
        if (!id) throw new Error("Missing station");
        const response = await setPointConfigurations({ pointOfSaleConfigurationId: id });
        if (response?.error !== false) throw new Error("Unconfirmed station");
        dispatch(updateCorporationPointConfig(selectedPoint));
        updateLoginData((prev) => ({ ...prev, point: selectedPoint }));
        setMenu("users");
        return true;
      } catch {
        setAccessError("No se ha podido abrir el puesto. Vuelve a seleccionarlo.");
        return false;
      }
    });
  const chooseUser = (employee) => {
    dispatch(setUserConfigData(employee));
    setUserDataLocal(employee);
    setAccessError("");
    setMenu("pin");
  };
  const back = (next) => {
    setUserDataLocal(null);
    setAccessError("");
    setMenu(next);
  };
  const renderMenu = () => {
    if (menu === "orderManager")
      return (
        <AccessSelector
          head="Selecciona tu puesto"
          title="Elige el punto de venta en el que vas a trabajar."
          data={orderManagers}
          icon={<BsTv />}
          callback={selectPoint}
          loading={pointsPending}
          feedback={accessError}
          onRetry={fetchOrderManagers}
        />
      );
    if (menu === "users")
      return (
        <AccessSelector
          head={defaultMenu === "users" ? "Cambiar empleado" : "Selecciona tu empleado"}
          title={`${point.name || "Punto de venta"} · Cada empleado accede con su propio PIN.`}
          data={assignedUsers}
          callback={chooseUser}
          feedback={accessError}
        />
      );
    if (menu === "pin")
      return (
        <PinPad
          key="employee"
          callback={handleUser}
          head={defaultMenu === "users" ? "Cambio de empleado" : "Acceso de empleado"}
          description={[userData?.fullname, point.name].filter(Boolean).join(" · ")}
          inputText="Introduce tu PIN de empleado de 4 dígitos para entrar."
          feedback={accessError}
        />
      );
    return (
      <PinPad
        key="platform"
        platform
        digits={6}
        callback={handlePassword}
        head="Accede al TPV"
        description={businessName}
        inputText="Introduce el código de plataforma de 6 dígitos para continuar."
        feedback={accessError}
      />
    );
  };
  return (
    <div className="tpvAccessFlow">
      <header className="tpvAccessBrand">
        {info.logo && <img src={info.logo} alt="Logotipo del negocio" />}
        <span>
          <strong>{businessName}</strong>
          <small>TERMINAL PUNTO DE VENTA</small>
        </span>
      </header>
      <main className="tpvAccessMain">
        {renderMenu()}
        {menu !== "password" && (
          <nav className="tpvAccessNavigation" aria-label="Opciones de acceso">
            {menu === "pin" && (
              <button
                type="button"
                className="tpvAccessSecondary"
                disabled={pending || loggingOut}
                onClick={() => back("users")}
              >
                Elegir otro empleado
              </button>
            )}
            {menu === "users" && (
              <button
                type="button"
                className="tpvAccessSecondary"
                disabled={pending || loggingOut}
                onClick={() => back("orderManager")}
              >
                Cambiar puesto
              </button>
            )}
            <button
              type="button"
              className="tpvAccessSecondary tpvAccessExit"
              disabled={pending || loggingOut}
              onClick={handleLogout}
            >
              {loggingOut ? "Cerrando sesión…" : "Cerrar sesión"}
            </button>
          </nav>
        )}
      </main>
    </div>
  );
};
